import type {
  PasswordUpdateStatus,
  PasswordUpdateEmailStatus,
} from "./password-update.type";
import type { StepBadgeStatus } from "@/ui/step-badge";
import type { ResetPasswordOutput } from "aws-amplify/auth";

import { Form } from "antd";
import { resetPassword, confirmResetPassword } from "aws-amplify/auth";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetCurrentUser } from "@/entities/current-user";
import { useAppNotification } from "@/modules/app-notification";

const { useForm } = Form;

export const usePasswordUpdate = () => {
  const [form] = useForm();
  const { t } = useTranslation();
  const { notify } = useAppNotification();

  const [emailStatus, setEmailStatus] = useState<PasswordUpdateEmailStatus>();
  const [updateStatus, setUpdateStatus] = useState<PasswordUpdateStatus>();

  const isSending = emailStatus === "sending";
  const isSent = emailStatus === "sent";
  const isSubmitting = updateStatus === "submitting";
  const isCompleted = updateStatus === "completed";

  const step1Status: StepBadgeStatus = isSent ? "finished" : "current";
  const step2Status: StepBadgeStatus = isCompleted
    ? "finished"
    : isSent
      ? "current"
      : null;

  const { currentUser } = useGetCurrentUser();
  const username = currentUser?.email ?? "";

  const onSendClick = async () => {
    try {
      setUpdateStatus(undefined);
      setEmailStatus("sending");
      const output = await resetPassword({ username });
      notifyNextStep(output);
    } catch (_error) {
      setEmailStatus(undefined);
      notify.error({ title: t("user:updatePassword.error") });
    }
  };

  const notifyNextStep = ({ nextStep }: ResetPasswordOutput) => {
    switch (nextStep.resetPasswordStep) {
      case "CONFIRM_RESET_PASSWORD_WITH_CODE":
        notify.success({ title: t("user:updatePassword.resetCode.success") });
        setEmailStatus("sent");
        break;
      case "DONE":
        notify.success({ title: t("user:updatePassword.success") });
        setEmailStatus(undefined);
        break;
    }
  };

  const onSubmit = async () => {
    try {
      setUpdateStatus("submitting");
      await confirmResetPassword({
        username,
        confirmationCode: form.getFieldValue("confirmationCode"),
        newPassword: form.getFieldValue("newPassword"),
      });
      setUpdateStatus("completed");
      notify.success({ title: t("user:updatePassword.success") });
    } catch (_error) {
      setUpdateStatus(undefined);
      notify.error({ title: t("user:updatePassword.error") });
    }
  };

  return {
    form,
    step1Status,
    step2Status,
    isSending,
    isSent,
    isSubmitting,
    onSendClick,
    onSubmit,
  };
};
